import React, { useState } from "react";
import Select from "react-select";
import { syncUserContragentMappingsFromFinansist } from "../../../api/timesheetApi";

const formatUserLabel = (user) =>
  [user.surname, user.name, user.patronymic].filter(Boolean).join(" ").trim();

const buildSyncSummary = (result) => {
  const parts = [
    `Создано привязок: ${result.created_mappings ?? 0}`,
    `Без привязки до синхронизации: ${result.total_users_without_mapping ?? 0}`,
  ];

  const duplicatesCount = result.duplicates?.length ?? 0;
  const unresolvedCount = result.unresolved?.length ?? 0;

  if (duplicatesCount > 0) {
    parts.push(`Дубликаты: ${duplicatesCount}`);
  }
  if (unresolvedCount > 0) {
    parts.push(`Не сопоставлено: ${unresolvedCount}`);
  }

  return parts.join(". ");
};

const FinansistContragentSync = ({ allUsers = [] }) => {
  const [syncForAllUsers, setSyncForAllUsers] = useState(true);
  const [syncForSelectedUsers, setSyncForSelectedUsers] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncHint, setSyncHint] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const handleSync = async () => {
    if (syncForSelectedUsers && selectedUsers.length === 0) {
      setSyncHint({
        type: "err",
        text: "Выберите хотя бы одного сотрудника для выборочной синхронизации.",
      });
      return;
    }

    setSyncLoading(true);
    setSyncHint(null);
    setSyncResult(null);

    try {
      const result = await syncUserContragentMappingsFromFinansist({
        userIds: syncForSelectedUsers
          ? selectedUsers.map((user) => user.value)
          : undefined,
      });

      setSyncResult(result);
      setSyncHint({
        type: "ok",
        text: buildSyncSummary(result),
      });
    } catch (err) {
      console.error(err);
      setSyncHint({
        type: "err",
        text: err?.message || "Не удалось синхронизировать контрагентов Finansist.",
      });
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <div className="salary-rebuild-cache">
      <p className="salary-rebuild-cache__text">
        Сопоставить пользователей портала с контрагентами Finansist по ФИО и
        создать привязки там, где их ещё нет.
      </p>
      <div className="salary-rebuild-cache__actions">
        <div className="salary-rebuild-cache__mode">
          <label className="salary-rebuild-cache__checkbox">
            <input
              type="checkbox"
              checked={syncForAllUsers}
              onChange={() => {
                setSyncForAllUsers(true);
                setSyncForSelectedUsers(false);
                setSelectedUsers([]);
                setSyncHint(null);
                setSyncResult(null);
              }}
            />
            <span>Синхронизировать всех</span>
          </label>
          <label className="salary-rebuild-cache__checkbox">
            <input
              type="checkbox"
              checked={syncForSelectedUsers}
              onChange={() => {
                setSyncForSelectedUsers(true);
                setSyncForAllUsers(false);
                setSyncHint(null);
                setSyncResult(null);
              }}
            />
            <span>Синхронизировать выборочно</span>
          </label>
        </div>
        {syncForSelectedUsers && (
          <div className="salary-rebuild-cache__users-select">
            <Select
              isMulti
              value={selectedUsers}
              onChange={(options) =>
                setSelectedUsers(Array.isArray(options) ? options : [])
              }
              options={allUsers.map((user) => ({
                value: user.id,
                label: formatUserLabel(user) || `ID ${user.id}`,
              }))}
              placeholder="Выберите сотрудников"
              noOptionsMessage={() => "Пользователи не найдены"}
            />
          </div>
        )}
        <button
          type="button"
          className="salary-rebuild-cache__btn"
          disabled={syncLoading}
          onClick={handleSync}
        >
          {syncLoading ? "Синхронизация…" : "Синхронизировать с Finansist"}
        </button>
        {syncHint && (
          <p
            className={`salary-rebuild-cache__hint salary-rebuild-cache__hint--${syncHint.type}`}
          >
            {syncHint.text}
          </p>
        )}
      </div>
      {syncResult && (
        <div className="finansist-contragent-sync__details">
          {(syncResult.items?.length ?? 0) > 0 && (
            <details className="finansist-contragent-sync__details-block">
              <summary>
                Созданные привязки ({syncResult.items.length})
              </summary>
              <ul>
                {syncResult.items.map((item) => (
                  <li key={`${item.user_id}-${item.finansist_contragent_id}`}>
                    {item.matched_fio || `Пользователь #${item.user_id}`} →{" "}
                    {item.finansist_contragent_name || item.finansist_contragent_id}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {(syncResult.duplicates?.length ?? 0) > 0 && (
            <details className="finansist-contragent-sync__details-block finansist-contragent-sync__details-block--warn">
              <summary>
                Дубликаты ({syncResult.duplicates.length})
              </summary>
              <ul>
                {syncResult.duplicates.map((item) => (
                  <li key={`dup-${item.user_id}`}>
                    {item.user_fio || `Пользователь #${item.user_id}`}:{" "}
                    {(item.contragent_names || item.contragent_ids || []).join(", ")}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {(syncResult.unresolved?.length ?? 0) > 0 && (
            <details className="finansist-contragent-sync__details-block finansist-contragent-sync__details-block--err">
              <summary>
                Не сопоставлено ({syncResult.unresolved.length})
              </summary>
              <ul>
                {syncResult.unresolved.map((item) => (
                  <li key={`unresolved-${item.user_id}`}>
                    {item.user_fio || `Пользователь #${item.user_id}`}:{" "}
                    {item.reason || "не найдено совпадение"}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default FinansistContragentSync;
